import { Fragment, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";
import BackArrow from "../assets/images/back-arrow.svg";
import FaceScanImg from "../assets/images/face-scan-img.png";

import {
  api_key,
  authEmployeeID,
  sdkURL,
  secret_key,
} from "../utils/constants";

const PulmoFaceScanInstruction = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const startScan = async () => {
    setLoading(true);
    setError("");
    try {
      const scanTokenResp = await fetch(sdkURL("/vitals/create-token"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: secret_key,
        },
        body: JSON.stringify({ api_key, employee_id: authEmployeeID }),
      });
      const scanTokenRespJSON = await scanTokenResp.json();
      if (scanTokenRespJSON?.statusCode?.toString?.().startsWith("2"))
        navigate("/pulmo-face-scan", {
          state: {
            scanToken: scanTokenRespJSON.scan_token,
            scanTokenExpiry: new Date(
              scanTokenRespJSON.token_expiry?.replace(" ", "T")
            ).getTime(),
          },
        });
      else
        throw new Error(
          scanTokenRespJSON?.message ?? "Error in Fetching the Scan Token"
        );
    } catch (err) {
      console.error(err);
      setError(err?.message ?? "Some error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-6 pt-2 pb-24 relative">
      <div className="flex items-center relative mb-8 tab-screen">
        <Link
          to={-1}
          className=" w-9 h-9 bg-[#2D2D2D] rounded-full absolute lg:left-6 md:left-6 left-0 flex flex-row justify-center items-center"
        >
          <img src={BackArrow} alt="" />
        </Link>
        <h2 className="text-xl font-medium text-white lg:text-2xl lg:ml-20 md:ml-20 ml-14">
          Instructions
        </h2>
      </div>
      <div className="flex flex-col items-center justify-center relative z-50">
        <img src={FaceScanImg} className="w-28 mb-3" alt="" />
        <div className=" bg-[#1D1D1D]/70 rounded-[20px] w-11/12 lg:w-8/12 md:w-8/12 m-auto p-6 lg:mt-6 md:mt-6">
          <h6 className=" text-base font-semibold text-secondary mb-3">
            Face Scan
          </h6>
          <div className="ml-0">
            <ol
              type="1"
              className="list-outside list-item text-xxs text-white font-light p-0 m-0"
            >
              <li className="mb-3 flex flex-row">
                <span className="mr-[2px]">1.</span> The ambient light should be
                bright and consistent and must be white.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">2.</span>Remove your masks, eye-wear,
                or clothing obstruction the face for the duration of scan.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">3.</span>Keep your face and phone
                steady to avoid movements during the scan.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">4.</span>For light mode: Make sure
                your face is mostly covered by the blue circle.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">5.</span>If prompted, allow the
                camera.
              </li>
            </ol>
          </div>
          <button
            className="mt-6 w-full flex items-center font-medium justify-center space-x-4 rounded-full outline-none px-5 py-2.5 bg-white disabled:bg-none disabled:bg-darkgray text-black mb-2"
            type="button"
            onClick={startScan}
            disabled={loading}
          >
            <span>Proceed</span>
            {loading && <Loader className="flex-shrink-0 h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="circle-blur w-[70%] h-56 mx-auto absolute top-24 left-0 right-0 blur-[100px]"></div>

      <Transition show={error.length > 0} as={Fragment}>
        <Dialog as={Fragment} onClose={() => {}}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/70 flex items-center justify-center"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl p-6 flex flex-col text-center">
                <Dialog.Description className="text-black font-normal text-sm">
                  {error}
                </Dialog.Description>
                <Link
                  className="mt-6 mx-auto px-4 py-2 rounded-full bg-gradient text-white text-xs font-medium"
                  to={-2}
                >
                  Back to Dashboard
                </Link>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>

      <div className="mt-6 p-5 rounded-[20px] bg-[#364560] text-white space-y-1.5 tab-screen lg:mt-6 md:mt-6">
        <h3 className="text-sm font-medium text-secondary">Disclaimer</h3>
        <p className="text-xxs text-justify font-light leading-4 text-white/80">
          For Investigational Use Only. Careplix is not a substitute for the
          clinical judgment of a health care professional. Careplix is intended
          to improve your awareness of general wellness. Careplix does not
          diagnose, treat, mitigate or prevent any disease, symptom, disorder or
          abnormal physical state. Consult with a health care professional or
          emergency services if you believe you may have a medical issue.
        </p>
      </div>
    </section>
  );
};

export default PulmoFaceScanInstruction;
